import { Component, computed, DestroyRef, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { SdTargetingFormComponent } from "../sd-targeting-form/sd-targeting-form.component";
import { SdTargetingFormModel } from "../sd-targeting-form/sd-targeting-form.types";
import { SdStrategyEx } from "@front/m19-models/strategy/SdStrategyEx";
import { IButtonDirective, MODAL_DATA, ModalRef, ToasterService } from "@front/m19-ui";
import { SdStrategyService } from "@front/m19-services";
import { SdTargetingType } from "@front/m19-models";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TacticType } from "@front/m19-api-client";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";

export interface SdTargetingModalCreationData {
  strategy?: SdStrategyEx;
  organizationId: number;
}

@Component({
  selector: "app-sd-targeting-modal-creation",
  imports: [SdTargetingFormComponent, IButtonDirective, TranslocoDirective],
  templateUrl: "./sd-targeting-modal-creation.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdTargetingModalCreationComponent {
  public readonly modalData = inject(MODAL_DATA) as SdTargetingModalCreationData;
  public readonly modalRef = inject(ModalRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sdStrategyService = inject(SdStrategyService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  loading = signal(false);
  readonly submitBtnLabel = computed(() => {
    return this.formValue()?.targetingType === SdTargetingType.PRODUCT_AI_TARGETING
      ? this.translocoService.translate("sd-targeting-add-popup.enable_ai-powered_targeting")
      : this.translocoService.translate("common.create");
  });

  formValue = signal<SdTargetingFormModel | undefined>(undefined);

  onFormSubmit() {
    if (!this.modalData.strategy || !this.modalData.organizationId || !this.formValue()?.targetingType) {
      return;
    }

    const value = this.formValue()!;

    switch (value.targetingType) {
      case SdTargetingType.PRODUCT:
        this.createProductTargeting(value);
        break;
      case SdTargetingType.PRODUCT_AI_TARGETING:
        this.enableAIPoweredTargeting(this.modalData.strategy, this.modalData.organizationId);
        break;
      case SdTargetingType.REMARKETING:
        this.createRemarketingTargeting(value, this.modalData.strategy);
        break;
    }
  }

  onFormChange(value: SdTargetingFormModel): void {
    this.formValue.set(value);
  }

  private createProductTargeting(value: SdTargetingFormModel): void {
    if (!this.modalData.strategy || !this.modalData.organizationId || !value.segment) {
      return;
    }

    const strategy = this.modalData.strategy;
    const tacticType = TacticType.LEGACY;
    this.loading.set(true);
    this.sdStrategyService
      .addTacticToStrategy(
        strategy.accountId,
        strategy.marketplace,
        strategy.strategyId,
        value.segment.segmentId,
        tacticType,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastrService.success("Product targeting created");
          this.modalRef.close();
        },
        error: (err) => {
          this.toastrService.error(err, "Product targeting creation error");
          this.loading.set(false);
        },
      });
  }

  private enableAIPoweredTargeting(strategy: SdStrategyEx, organizationId: number): void {
    this.loading.set(true);
    this.sdStrategyService
      .updateStrategyAutoAlgoExploration(
        strategy.accountId,
        strategy.marketplace,
        organizationId,
        strategy.strategyId,
        true,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastrService.success("AI-powered targeting enabled");
          this.modalRef.close();
        },
        error: (err) => {
          this.toastrService.error(err, "AI-powered targeting error");
          this.loading.set(false);
        },
      });
  }

  private createRemarketingTargeting(value: SdTargetingFormModel, strategy: SdStrategyEx): void {
    if (!value.audienceExpressionType || !value.audienceMatchType || !value.audienceLookback) {
      return;
    }

    this.loading.set(true);
    this.sdStrategyService
      .addRemarketingAudienceToSdStrategy(
        strategy.accountId,
        strategy.marketplace,
        strategy.strategyId,
        value.audienceExpressionType,
        value.audienceMatchType,
        value.audienceLookback,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastrService.success("Remarketing Targeting sucessfully added to strategy");
          this.modalRef.close();
        },
        error: (err) => {
          this.toastrService.error(err, "Remarketing Targeting creation error");
          this.loading.set(false);
        },
      });
  }
}
